import convert, { Unit } from 'convert-units';
import { Base_Unit, Base_Unit_Type_Enum, Billing_Type_Enum, Measurement_Unit_Enum, Order_Item, Packaging, Trade_Item_Unit_Descriptor_Enum } from '@kheops/graphql';
import PackagingI18n from './assets/i18n/packaging-imports.js';

export type SupportedLocales = 'fr';
export type BaseUnitTypeIntl = 'bag' | 'bag_in_box' | 'bottle' | 'box' | 'carton' | 'cup' | 'jar' | 'pack' | 'piece' | 'slice' | 'tin' | 'tube' | 'box_set' | 'spray' | 'bulk' | 'flask' | 'sachet' | 'tray' | 'keg' | 'can' | 'net' | 'tub';

export const BaseUnitTypeIntlMap: {[key in Base_Unit_Type_Enum]: BaseUnitTypeIntl} = {
  BAG: 'bag',
  BAG_IN_BOX: 'bag_in_box',
  BOTTLE: 'bottle',
  BOX: 'box',
  CARTON: 'carton',
  CUP: 'cup',
  JAR: 'jar',
  PACK: 'pack',
  PIECE: 'piece',
  SLICE: 'slice',
  TIN: 'tin',
  TUBE: 'tube',
  BOX_SET: 'box_set',
  SPRAY: 'spray',
  BULK: 'bulk',
  FLASK: 'flask',
  SACHET: 'sachet',
  TRAY: 'tray',
  KEG: 'keg',
  CAN: 'can',
  NET: 'net',
  TUB: 'tub',
};

export type BaseUnit = Pick<Base_Unit, 'unit_type' | 'unit_net_content' | 'content_measurement_unit' | 'unit_quantity' | 'billing_type'>;

export type TradeItemUnitDescriptorIntl = 'base_unit' | 'case' | 'pallet' | 'per_unit' | 'crate' | 'display' | 'sack' | 'half_case';

export const TradeItemUnitDescriptorIntlMap: {[key in Trade_Item_Unit_Descriptor_Enum]: TradeItemUnitDescriptorIntl } = {
  CASE: 'case',
  PALLET: 'pallet',
  UNIT: 'per_unit',
  CRATE: 'crate',
  DISPLAY: 'display',
  SACK: 'sack',
  HALF_CASE: 'half_case',
};

export type LogisticalPackaging = Pick<Packaging, 'trade_item_unit_descriptor' | 'unit_quantity' | 'content_measurement_unit' | 'net_content' | 'price'>;

export type LightLogisticalPackaging = Omit<LogisticalPackaging, 'trade_item_unit_descriptor'>

export interface ValueWithUnit {
  value: number;
  unit: Measurement_Unit_Enum;
}

export type TranslationFunction<T> = (key: T) => string | Promise<string>;

export interface NumberFormatter {
  format: (value: number) => string;
}

export interface ConvertUnits {
  convertUnit?: convert.Unit;
  volumeUnit: Measurement_Unit_Enum;
}

export type OrderItem = Pick<Order_Item, 'quantity' | 'weight' | 'price'> & {
  packaging: LogisticalPackaging;
};

export function FormatMeasurementUnit(
  measurementUnit: Measurement_Unit_Enum,
  locale: SupportedLocales,
  count?: number | null
): string {
  switch (measurementUnit) {
    case Measurement_Unit_Enum.L:
      return measurementUnit;
    case Measurement_Unit_Enum.Unit:
      return new Intl.PluralRules(locale).select(count!) === 'one'
        ? PackagingI18n[locale].unit_one
        : PackagingI18n[locale].unit_other;
    default:
      return measurementUnit.toLowerCase();
  }
}

export function FormatBaseUnitQuantity(
  baseUnit: BaseUnit,
  locale: SupportedLocales,
  numberFormatter: NumberFormatter,
): string {
  if (baseUnit.unit_type !== Base_Unit_Type_Enum.Bulk) {
    const baseUnitNetContent = numberFormatter.format(baseUnit.unit_net_content!);

    const i18nBaseUnitType = PackagingI18n[locale][BaseUnitTypeIntlMap[baseUnit.unit_type!]];

    const contentMeasurementUnit = FormatMeasurementUnit(
      baseUnit.content_measurement_unit!,
      locale,
      baseUnit.unit_net_content
    );

    return (
      `${
        baseUnit.unit_quantity! !== 1
          ? `${baseUnit.unit_quantity}x `
          : ''
      }${i18nBaseUnitType} ${
        baseUnit.billing_type === Billing_Type_Enum.WeightDependent ? '~' : ''
      }${baseUnitNetContent}${
        baseUnit.content_measurement_unit === Measurement_Unit_Enum.Unit
          ? ' '
          : ''
      }${contentMeasurementUnit}`
    );
  }

  return PackagingI18n[locale].bulk;
}

export function FormatLogisticalPackaging(
  packaging: LogisticalPackaging,
  locale: SupportedLocales,
  baseUnit?: BaseUnit,
): string {
  const i18nTiud = PackagingI18n[locale][TradeItemUnitDescriptorIntlMap[packaging.trade_item_unit_descriptor]];

  if (packaging.trade_item_unit_descriptor === Trade_Item_Unit_Descriptor_Enum.Unit) {
    return i18nTiud;
  }

  let result = `${i18nTiud} ${PackagingI18n[locale].of} `;

  if (baseUnit?.unit_type === Base_Unit_Type_Enum.Bulk) {
    const measurementUnit = FormatMeasurementUnit(packaging.content_measurement_unit!, locale, packaging.net_content);
    const shouldAddSpaceBeforeMesurementUnit = packaging.content_measurement_unit === Measurement_Unit_Enum.Unit;
    result += `${packaging.net_content}${shouldAddSpaceBeforeMesurementUnit ? ' ': ''}${measurementUnit}`
  } else {
    result += packaging.unit_quantity;
  }

  return result;
}

export function FormatPackagingUnitPrice(packaging: LogisticalPackaging): number {
  return packaging.price / (packaging.unit_quantity ?? packaging.net_content)!;
}

export function GetConvertedUnit(measurementUnit: Measurement_Unit_Enum): ConvertUnits {
  let volumeUnit = Measurement_Unit_Enum.Unit;
  let convertUnit: convert.Unit | undefined;

  if (measurementUnit !== Measurement_Unit_Enum.Unit) {
    // Tonne should be treated as Metric Tonne by convert-units
    convertUnit = measurementUnit === Measurement_Unit_Enum.T ? 'mt' : measurementUnit.toString().toLowerCase() as Unit;
    const { measure } = convert().describe(convertUnit);

    if (measure === 'mass') {
      volumeUnit = Measurement_Unit_Enum.Kg;
    } else if (measure === 'volume') {
      volumeUnit = Measurement_Unit_Enum.L;
    }
  }

  return {
    convertUnit,
    volumeUnit,
  };
}

export function getConvertedQuantity(quantityToConvert: number, measurementUnit: Measurement_Unit_Enum): ValueWithUnit {
  const convertedUnits = GetConvertedUnit(measurementUnit);
  const unit = convertedUnits.volumeUnit;

  if (measurementUnit !== Measurement_Unit_Enum.Unit) {
    return {
      value: convert(quantityToConvert)
        .from(convertedUnits.convertUnit!)
        .to(unit.toString().toLowerCase() as Unit),
      unit,
    };
  }

  return {
    value: quantityToConvert,
    unit,
  };
}

export function FormatPackagingVolumePrice(packaging: LightLogisticalPackaging, baseUnit: BaseUnit): ValueWithUnit {
  const baseUnitTypeIsBulk = baseUnit.unit_type === Base_Unit_Type_Enum.Bulk;
  let measurementUnit: Measurement_Unit_Enum;
  let quantityToConvert: number;

  if (baseUnitTypeIsBulk) {
    measurementUnit = packaging.content_measurement_unit!;
    quantityToConvert = packaging.net_content!;
  } else {
    measurementUnit = baseUnit.content_measurement_unit!;
    quantityToConvert = baseUnit.unit_net_content!;
  }

  const { value: convertedQuantity, unit } = getConvertedQuantity(quantityToConvert, measurementUnit);

  if (baseUnitTypeIsBulk) {

    return {
      value: packaging.price / convertedQuantity,
      unit,
    };
  }

  return {
    value:
      packaging.price
      / (baseUnit.unit_quantity!
        * convertedQuantity
        * packaging.unit_quantity!),
    unit,
  };
}

/**
 * Given a packaging with its base unit and a volume price, computes the new packaging price.
 */
export function ComputePackagingPriceFromVolumePrice(packaging: LogisticalPackaging, baseUnit: BaseUnit, volumePrice: number): number {
  const baseUnitTypeIsBulk = baseUnit.unit_type === Base_Unit_Type_Enum.Bulk;
  const quantityToConvert =  baseUnitTypeIsBulk ?  packaging.net_content! : baseUnit.unit_net_content!;
  const  { value: convertedQuantity } = getConvertedQuantity(quantityToConvert, baseUnitTypeIsBulk ? packaging.content_measurement_unit! : baseUnit.content_measurement_unit!);

  return  baseUnitTypeIsBulk
    ? volumePrice * convertedQuantity
    : volumePrice * (
      baseUnit.unit_quantity!
      * convertedQuantity
      * packaging.unit_quantity!
    );
}

export function formatPriceByBillingType(price: string, billingType: Billing_Type_Enum): string {
  const pricePrefix = billingType === Billing_Type_Enum.WeightDependent ? '~' : '';

  return `${pricePrefix}${price}`;
}

export function ComputePackagingWeight(packaging: LightLogisticalPackaging, baseUnit: BaseUnit): ValueWithUnit {
  const isItBulk = baseUnit?.unit_type === Base_Unit_Type_Enum.Bulk;
  const valueToConvert = isItBulk ? packaging.net_content! : packaging.unit_quantity! * baseUnit.unit_quantity! * baseUnit.unit_net_content!;
  const unitToConvert = isItBulk ? packaging.content_measurement_unit! : baseUnit.content_measurement_unit!;

  return getConvertedQuantity(valueToConvert, unitToConvert);
}

export function ComputePackagingPriceFromOrderItem(orderItem: OrderItem, baseUnit: BaseUnit): number {
  if (!orderItem.price || !orderItem.quantity) {
    return 0;
  }

  if (baseUnit.billing_type === Billing_Type_Enum.WeightDependent) {
    const volumePrice = orderItem.price / orderItem.weight!;

    return ComputePackagingPriceFromVolumePrice(orderItem.packaging, baseUnit, volumePrice);
  }

  return orderItem.price / orderItem.quantity;
}

export function getConvertedSuggestedRetailPrice(suggestedRetailPrice: number, netContent: number, measurementUnit: Measurement_Unit_Enum): number {
  const convertedQuantity = getConvertedQuantity(netContent, measurementUnit);

  return suggestedRetailPrice * convertedQuantity.value;
}

export interface BaseUnitInfo {
  suggestedRetailPrice: number;
  unitType: Base_Unit_Type_Enum;
  billingType: Billing_Type_Enum;
  netContent?: number;
  measurementUnit?: Measurement_Unit_Enum;
}

export function formatSuggestedRetailPrice(baseUnitInfo: BaseUnitInfo): string {
  const formatter = new Intl.NumberFormat('fr', {
    style: 'currency',
    currency: 'EUR',
  }).format;

  if (baseUnitInfo.unitType === Base_Unit_Type_Enum.Bulk) {
    const unit = getConvertedQuantity(baseUnitInfo.netContent!, baseUnitInfo.measurementUnit!).unit;

    return `${formatter(baseUnitInfo.suggestedRetailPrice)}/${FormatMeasurementUnit(unit, 'fr', 1)}`;
  }

  if (baseUnitInfo.billingType === Billing_Type_Enum.WeightDependent) {
    const convertedSuggestedRetailPrice = getConvertedSuggestedRetailPrice(baseUnitInfo.suggestedRetailPrice, baseUnitInfo.netContent!, baseUnitInfo.measurementUnit!);

    return formatPriceByBillingType(formatter(convertedSuggestedRetailPrice), baseUnitInfo.billingType);
  }

  return formatter(baseUnitInfo.suggestedRetailPrice);
}
